import {
    Box,
    Grid,
    Typography,
    Avatar,
    Stack,
    Chip,
    Button,
    Paper,
    Modal,
    Skeleton
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { useNavigate } from "react-router-dom";
import { ReactComponent as VerifiedTick } from '../../../assets/podcast_monster/verified-tick.svg';
import MailOutlinedIcon from '@mui/icons-material/MailOutlined';
import EastOutlinedIcon from '@mui/icons-material/EastOutlined';
import { useGetUserDetailsMutation } from "redux/api/authApi";
import { getCookie } from "utils/storageUtils";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useGetGeneratedEpisodeListingsMutation } from "redux/api/promptApi";
import { isMobile } from "react-device-detect";

const interFontStyle = (
    <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        .myprofile-root, .myprofile-root * { font-family: 'Inter', sans-serif !important; }
    `}</style>
);

const SkillChip = ({ label }: { label: string }) => (
    <Chip
        label={label}
        size="small"
        sx={{
            background: '#2B2F36',
            color: "#e5e7eb",
            fontSize: 11,
            height: 22,
            borderRadius: 4,
            fontFamily: 'Inter, sans-serif',
        }}
    />
);

/* -------------------------------- COURSE CARD -------------------------------- */
const CourseCard = ({ course }: any) => {
    const navigate = useNavigate();

    return (
        <Paper
            sx={{
                p: 1.5,
                borderRadius: "24px",
                background: 'transparent',
                border: "1px solid #2D2D2D",
                height: "100%",
                fontFamily: 'Inter, sans-serif',
            }}
        >
            <Box sx={{ position: "relative" }}>
                <Box
                    component="img"
                    src={course.image_path_small}
                    sx={{
                        width: "100%",
                        height: 232,
                        objectFit: "cover",
                        borderRadius: "16px",
                        cursor: 'pointer',
                    }}
                    alt={course.title}
                    onClick={() => navigate('/codegenie/course-detail', { state: { courseId: course._id } })}
                />
                {course.bestseller && (
                    <Chip
                        label="BESTSELLER"
                        size="small"
                        sx={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            height: 20,
                            fontSize: 10,
                            background: "#6b7280",
                            color: "#fff",
                            fontFamily: 'Inter, sans-serif',
                        }}
                    />
                )}
            </Box>

            <Typography
                mt={1.5}
                fontSize={13}
                fontWeight={600}
                color="#fff"
                sx={{ cursor: 'pointer', fontFamily: 'Inter, sans-serif', lineHeight: 1.4 }}
                onClick={() => navigate('/codegenie/course-detail', { state: { courseId: course._id } })}
            >
                {course.title}
            </Typography>

            <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" gap={0.5}>
                {course?.tags?.map((t: string) => (
                    <Chip
                        key={t}
                        label={t}
                        size="small"
                        sx={{
                            height: 18,
                            fontSize: 10,
                            border: '1px solid #2D2D2D',
                            background: 'transparent',
                            color: "#cbd5f5",
                            borderRadius: '2px',
                            fontFamily: 'Inter, sans-serif',
                        }}
                    />
                ))}
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center" mt={1}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        px: 0.8,
                        py: 0.2,
                        border: "1px solid #374151",
                        borderRadius: "6px",
                        background: "transparent",
                    }}
                >
                    {[1, 2, 3, 4, 5].map((i) => (
                        <StarIcon
                            key={i}
                            sx={{
                                fontSize: 14,
                                color: i <= Math.round(4.2) ? "#facc15" : "#374151",
                            }}
                        />
                    ))}
                    <Typography fontSize="13px" color="#fff" ml={0.3} fontFamily="Inter, sans-serif">
                        4.2
                    </Typography>
                </Box>
                <Box
                    sx={{
                        px: 0.8,
                        py: 0.2,
                        border: "1px solid #374151",
                        borderRadius: "6px",
                    }}
                >
                    <Typography fontSize="11px" color="#9CA3AF" fontFamily="Inter, sans-serif">
                        154 ratings
                    </Typography>
                </Box>
            </Stack>

            <Stack direction="row" spacing={1} mt={1} alignItems="center">
                <Typography color="#fff" fontWeight={700} fontSize={14} fontFamily="Inter, sans-serif">
                    ${course.suggested_price + ' USD'}
                </Typography>
                <Typography
                    fontSize={12}
                    color="#9CA3AF"
                    fontFamily="Inter, sans-serif"
                    sx={{ textDecoration: "line-through" }}
                >
                    $399
                </Typography>
                <EastOutlinedIcon
                    sx={{ color: '#0168B5', cursor: 'pointer', fontSize: 18, ml: 'auto' }}
                    onClick={() => navigate('/codegenie/course-detail', { state: { courseId: course._id } })}
                />
            </Stack>
        </Paper>
    );
};

const CourseCardSkeleton = () => (
    <Paper
        sx={{
            p: 1.5,
            borderRadius: "24px",
            background: "transparent",
            border: "1px solid #2D2D2D",
        }}
    >
        <Skeleton variant="rectangular" height={232} sx={{ borderRadius: "16px" }} />
        <Skeleton variant="text" height={28} sx={{ mt: 1 }} />
        <Stack direction="row" spacing={1} mt={1}>
            <Skeleton variant="rounded" width={60} height={18} />
            <Skeleton variant="rounded" width={60} height={18} />
        </Stack>
        <Stack direction="row" spacing={1} mt={1}>
            <Skeleton variant="rounded" width={100} height={24} />
            <Skeleton variant="rounded" width={80} height={24} />
        </Stack>
        <Skeleton variant="text" width={120} sx={{ mt: 1 }} />
    </Paper>
);

export interface YouTubeEmbedOptions {
    autoplay?: boolean;
    rel?: boolean;
    modestbranding?: boolean;
    start?: number;
    end?: number;
    cc?: boolean;
}

export function toYouTubeEmbed(url: string | null, opts: YouTubeEmbedOptions = {}): string | null {
    if (!url) return null;
    let id: string | null = null;
    try {
        const u = new URL(url.trim());
        if (u.hostname.includes('youtu.be')) {
            id = u.pathname.slice(1) || null;
        }
        if (!id && (u.hostname.includes('youtube.com') || u.hostname.includes('www.youtube.com'))) {
            if (u.searchParams.has('v')) {
                id = u.searchParams.get('v');
            } else {
                const embedMatch = u.pathname.match(/\/embed\/([^/?]+)/);
                const shortsMatch = u.pathname.match(/\/shorts\/([^/?]+)/);
                if (embedMatch) id = embedMatch[1];
                else if (shortsMatch) id = shortsMatch[1];
            }
        }
        if (!id) return null;
        const params = new URLSearchParams();
        if (opts.autoplay) params.set('autoplay', '1');
        if (opts.rel === false) params.set('rel', '0');
        else if (opts.rel === true) params.set('rel', '1');
        if (opts.modestbranding) params.set('modestbranding', '1');
        if (typeof opts.start === 'number' && opts.start > 0) params.set('start', String(Math.floor(opts.start)));
        if (typeof opts.end === 'number' && opts.end > 0) params.set('end', String(Math.floor(opts.end)));
        if (opts.cc) params.set('cc_load_policy', '1');
        const query = params.toString();
        return `https://www.youtube.com/embed/${encodeURIComponent(id)}${query ? `?${query}` : ''}`;
    } catch (_err) {
        return null;
    }
}

/* -------------------------------- PAGE -------------------------------- */
export default function Myprofilepage() {

    const navigate = useNavigate();
    const supportUserid = process.env.REACT_APP_SUPPORT_USER_ID || '';
    const loggedIn = localStorage.getItem('logged_in');
    const useremail = localStorage.getItem('user_email') || getCookie('user_email') || '';
    const [userDetails, setUserDetails] = useState<any>('');
    const [showFullBio, setShowFullBio] = useState(false);
    const [courses, setCourses] = useState<any>();
    const [loadingCourses, setLoadingCourses] = useState<boolean>(false);
    const [showAllCourses, setShowAllCourses] = useState(false);
    const [generatedEpisodes, setGeneratedEpisodes] = useState<any>();
    const [loadingEpisodes, setLoadingEpisodes] = useState<boolean>(false);
    const [getGeneratedEpisodeListingsMutation] = useGetGeneratedEpisodeListingsMutation();
    const [helperPopup, setHelperPopup] = useState<boolean>(false);
    const [embedSrc, setEmbedSrc] = useState('');
    const [helpervideosrc, setHelperVideoSrc] = useState<any>();
    const [openMenuId, setOpenMenuId] = useState(null);
    const [userLoading, setUserLoading] = useState<boolean>(false);

    const [getUserDetailsMutation] = useGetUserDetailsMutation();

    const fetchData = async () => {
        try {
            setUserLoading(true);
            const response = await getUserDetailsMutation({ email: useremail, filter: '', start_date: '', end_date: '' });
            if ('data' in response) {
                setUserDetails(response.data.data);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setUserLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";

    const getCourselisting = async () => {
        setLoadingCourses(true);
        try {
            const res = await axios.get(`${apiUrl}/api/prompts/courses/by-user?user_email=${useremail}`);
            setCourses(res?.data?.courses);
        } catch (error: any) {
            console.log('Error fetching courses listing=>', error);
            toast.error(error);
        } finally {
            setLoadingCourses(false);
        }
    };

    useEffect(() => {
        getCourselisting();
    }, []);

    const bio = userDetails?.user_bio || "";
    const minChars = 120;
    const displayedBio = showFullBio ? bio : bio.slice(0, minChars);
    const visibleCourses = showAllCourses ? courses : courses?.slice(0, 3);

    const fetchEpisodeData = async () => {
        try {
            setLoadingEpisodes(true);
            const response = await getGeneratedEpisodeListingsMutation({
                email: useremail,
                limitval: 12,
                skipval: 0,
                search: '',
                fromDate: undefined,
                toDate: undefined
            });
            if ('data' in response) {
                const { data } = response;
                setGeneratedEpisodes(data);
            } else if ('error' in response) {
                const { error } = response;
            }
        } catch (error: any) {
            console.log('Error fetching episodes=>', error);
        } finally {
            setLoadingEpisodes(false);
        }
    };

    useEffect(() => {
        fetchEpisodeData();
    }, []);

    const handleVideoClick = (url: string) => {
        const tmpurl = toYouTubeEmbed(url);
        setEmbedSrc(tmpurl ?? '');
        setHelperVideoSrc(url);
        setHelperPopup(true);
    };

    const handleClose = () => {
        setOpenMenuId(null);
    };

    return (
        <>
            {interFontStyle}

            <Box
                className="overlay-container myprofile-root"
                sx={{
                    minHeight: "100vh",
                    fontFamily: 'Inter, sans-serif',
                }}
            >
                <Box
                    sx={{
                        px: { xs: 2, md: 6 },
                        py: 4,
                        maxWidth: 1100,
                        mx: "auto",
                    }}
                >

                    {/* ================= PROFILE HEADER — single flat card, matches Figma ================= */}
                    <Box
                        sx={{
                            p: "24px",
                            mb: 4,
                            borderRadius: "16px",
                            background: "transparent",
                            
                        }}
                    >
                        <Grid container spacing={3}>

                            {/* LEFT: Avatar + Info */}
                            {userLoading ? (
                                <Grid item xs={12} md={8}>
                                    <Stack direction="row" spacing={2.5}>
                                        <Skeleton
                                            variant="rounded"
                                            width={132}
                                            height={132}
                                            sx={{ borderRadius: "8px", flexShrink: 0 }}
                                        />
                                        <Box width="100%">
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                <Skeleton variant="text" width={140} height={28} />
                                                <Skeleton variant="rounded" width={90} height={20} />
                                            </Stack>
                                            <Stack direction="row" spacing={4} mt={1.5}>
                                                <Box>
                                                    <Skeleton variant="text" width={50} />
                                                    <Skeleton variant="rounded" width={60} height={18} sx={{ mt: 0.5 }} />
                                                </Box>
                                                <Box>
                                                    <Skeleton variant="text" width={90} />
                                                    <Skeleton variant="text" width={60} />
                                                </Box>
                                                <Box>
                                                    <Skeleton variant="text" width={70} />
                                                    <Skeleton variant="text" width={60} />
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Stack>
                                    <Box mt={2}>
                                        <Skeleton variant="text" width={100} />
                                        <Skeleton variant="text" />
                                        <Skeleton variant="text" />
                                        <Skeleton variant="text" width="80%" />
                                    </Box>
                                </Grid>
                            ) : (
                                <Grid item xs={12} md={8}>
                                    <Stack direction="row" spacing={2.5} alignItems="flex-start">

                                        {/* Avatar 132×132, borderRadius 8px */}
                                        <Avatar
                                            src={userDetails?.user_image}
                                            sx={{
                                                width: 132,
                                                height: 132,
                                                borderRadius: "8px",
                                                flexShrink: 0,
                                            }}
                                        />

                                        <Box>
                                            {/* Name + INSTRUCTOR chip */}
                                            <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                                                <Typography
                                                    color="#fff"
                                                    fontWeight={700}
                                                    fontSize={18}
                                                    fontFamily="Inter, sans-serif"
                                                >
                                                    {userDetails?.name}
                                                </Typography>
                                                <Chip
                                                    label="INSTRUCTOR"
                                                    size="small"
                                                    icon={<VerifiedTick />}
                                                    sx={{
                                                        height: 18,
                                                        fontSize: 10,
                                                        backgroundColor: "#2B2F36",
                                                        color: "#ffffff",
                                                        borderRadius: "4px",
                                                        px: 0.5,
                                                        fontFamily: 'Inter, sans-serif',
                                                        "& .MuiChip-icon": {
                                                            color: "#60A5FA",
                                                            ml: "4px",
                                                            mr: "-2px",
                                                        },
                                                    }}
                                                />
                                            </Stack>

                                            {/* Skills | Total learners | Reviews */}
                                            <Stack direction="row" alignItems="flex-start">
                                                {/* Skills */}
                                                <Box sx={{ pr: 4, borderRight: '1px solid #2e3140' }}>
                                                    <Typography
                                                        fontSize={12}
                                                        color="#9aa0b4"
                                                        mb={0.5}
                                                        fontFamily="Inter, sans-serif"
                                                    >
                                                        Skills
                                                    </Typography>
                                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                                                        {(userDetails?.user_skills || '')
                                                            .split(',')
                                                            .map((s: string) => s.trim())
                                                            .filter(Boolean)
                                                            .map((skill: string) => (
                                                                <SkillChip key={skill} label={skill} />
                                                            ))}
                                                    </Stack>
                                                </Box>

                                                {/* Total learners */}
                                                <Box sx={{ px: 4, borderRight: '1px solid #2e3140' }}>
                                                    <Typography
                                                        fontSize={12}
                                                        color="#9aa0b4"
                                                        fontFamily="Inter, sans-serif"
                                                    >
                                                        Total learners
                                                    </Typography>
                                                    <Typography
                                                        fontSize={16}
                                                        color="#fff"
                                                        fontWeight={700}
                                                        fontFamily="Inter, sans-serif"
                                                    >
                                                        {userDetails?.total_learners}
                                                    </Typography>
                                                </Box>

                                                {/* Reviews */}
                                                <Box sx={{ pl: 4 }}>
                                                    <Typography
                                                        fontSize={12}
                                                        color="#9aa0b4"
                                                        fontFamily="Inter, sans-serif"
                                                    >
                                                        Reviews
                                                    </Typography>
                                                    <Typography
                                                        fontSize={16}
                                                        color="#fff"
                                                        fontWeight={700}
                                                        fontFamily="Inter, sans-serif"
                                                    >
                                                        {userDetails?.total_reviews_count}
                                                    </Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                    </Stack>

                                    {/* About me */}
                                    <Box mt={2.5}>
                                        <Typography
                                            fontSize={13}
                                            color="#fff"
                                            fontWeight={700}
                                            mb={0.5}
                                            fontFamily="Inter, sans-serif"
                                        >
                                            About me
                                        </Typography>
                                        <Typography
                                            fontSize={13}
                                            color="#cfd3e1"
                                            lineHeight={1.7}
                                            fontFamily="Inter, sans-serif"
                                            sx={{ textAlign: 'justify' }}
                                        >
                                            {displayedBio}
                                            {bio.length > minChars && (
                                                <Typography
                                                    component="span"
                                                    color="#60A5FA"
                                                    fontFamily="Inter, sans-serif"
                                                    sx={{ cursor: "pointer", ml: 0.5 }}
                                                    onClick={() => setShowFullBio(!showFullBio)}
                                                >
                                                    {showFullBio ? " less..." : " more..."}
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}

                            {/* RIGHT: Follow + Message */}
                            <Grid item xs={12} md={4}>
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    justifyContent={{ xs: "flex-start", md: "flex-end" }}
                                >
                                    <Button
                                        size="small"
                                        sx={{
                                            textTransform: "none",
                                            background: "#DE2900",
                                            color: "#fff",
                                            borderRadius: "6px !important",
                                            px: 2,
                                            fontFamily: 'Inter, sans-serif',
                                            fontWeight: 600,
                                            "&:hover": { background: '#DE2900' }
                                        }}
                                    >
                                        + Follow
                                    </Button>

                                    <Button
                                        size="small"
                                        className="button_leniar px-3"
                                        sx={{
                                            textTransform: "none",
                                            color: "#fff",
                                            borderRadius: "6px !important",
                                            gap: '3px',
                                            alignItems: 'center',
                                            display: 'flex',
                                            fontFamily: 'Inter, sans-serif',
                                            fontWeight: 600,
                                        }}
                                    >
                                        <MailOutlinedIcon sx={{ fontSize: '18px' }} />Message
                                    </Button>
                                </Stack>
                            </Grid>

                        </Grid>
                    </Box>
                    {/* /PROFILE HEADER */}

                    {/* ================= MY COURSES ================= */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography color="#fff" fontWeight={600} fontFamily="Inter, sans-serif">
                            My courses ({courses?.length})
                        </Typography>
                        {courses?.length > 3 && (
                            <Typography
                                fontSize={12}
                                color="#60A5FA"
                                fontFamily="Inter, sans-serif"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => setShowAllCourses((prev) => !prev)}
                            >
                                {showAllCourses ? "View less" : "View more"}
                            </Typography>
                        )}
                    </Stack>

                    <Grid container spacing={2} mb={4}>
                        {loadingCourses
                            ? Array.from(new Array(3)).map((_, i) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <CourseCardSkeleton />
                                </Grid>
                            ))
                            : visibleCourses?.map((c: any, i: any) => (
                                <Grid item xs={12} sm={6} md={4} key={i}>
                                    <CourseCard course={c} />
                                </Grid>
                            ))}
                    </Grid>

                    {/* ================= PODCAST ================= */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography color="#fff" fontWeight={600} fontFamily="Inter, sans-serif">
                            Podcast {`(${generatedEpisodes?.length})`}
                        </Typography>
                        <Typography
                            fontSize={12}
                            color="#60A5FA"
                            fontFamily="Inter, sans-serif"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => {
                                localStorage.setItem('activeItem', 'episodes');
                                navigate('/codegenie/generated-blogs');
                            }}
                        >
                            View more
                        </Typography>
                    </Stack>

                    <Paper
                        sx={{
                            p: 2.5,
                            borderRadius: "16px",
                            background: '#1a1c24',
                            border: "1px solid #2e3140",
                            fontFamily: 'Inter, sans-serif',
                        }}
                    >
                        {/* Featured podcast */}
                        {loadingEpisodes ? (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Skeleton variant="rounded" height={232} sx={{ borderRadius: "16px" }} />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Skeleton variant="text" height={28} width="80%" />
                                    <Skeleton variant="text" height={18} sx={{ mt: 1 }} />
                                    <Skeleton variant="text" height={18} />
                                    <Skeleton variant="text" height={18} width="60%" />
                                    <Skeleton variant="rounded" width={120} height={32} sx={{ mt: 2 }} />
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={4}>
                                    <Box
                                        component="img"
                                        src={generatedEpisodes?.[0]?.thump_image_path}
                                        sx={{
                                            width: "100%",
                                            height: 232,
                                            objectFit: "cover",
                                            borderRadius: "16px",
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            localStorage.setItem('activeItem', 'episodes');
                                            navigate('/codegenie/generated-blogs');
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Typography
                                        color="#fff"
                                        fontWeight={600}
                                        mb={1}
                                        fontFamily="Inter, sans-serif"
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            localStorage.setItem('activeItem', 'episodes');
                                            navigate('/codegenie/generated-blogs');
                                        }}
                                    >
                                        {generatedEpisodes?.[0]?.title}
                                    </Typography>
                                    <Typography
                                        fontSize={13}
                                        color="#9CA3AF"
                                        lineHeight={1.7}
                                        fontFamily="Inter, sans-serif"
                                    >
                                        {generatedEpisodes?.[0]?.description}
                                    </Typography>
                                    <Button
                                        size="small"
                                        className="button_leniar px-3"
                                        sx={{
                                            mt: 2,
                                            textTransform: "none",
                                            color: "#fff",
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            cursor: 'pointer',
                                            fontFamily: 'Inter, sans-serif',
                                        }}
                                        onClick={() => {
                                            handleClose();
                                            handleVideoClick(
                                                generatedEpisodes?.[0]?.source === "youtube"
                                                    ? generatedEpisodes?.[0]?.url
                                                    : generatedEpisodes?.[0]?.original_file
                                            );
                                        }}
                                    >
                                        View Podcast <EastOutlinedIcon />
                                    </Button>
                                </Grid>
                            </Grid>
                        )}

                        {/* Podcast thumbnail grid */}
                        {loadingEpisodes ? (
                            <Grid container spacing={3} mt={1}>
                                {Array.from(new Array(4)).map((_, index) => (
                                    <Grid item xs={12} md={3} key={index}>
                                        <Skeleton
                                            variant="rounded"
                                            height={160}
                                            width="100%"
                                            animation="wave"
                                            sx={{ borderRadius: "16px" }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        ) : (
                            <Grid container spacing={3} mt={1}>
                                {generatedEpisodes?.slice(0, 4).map((data: any, index: any) => (
                                    <Grid item xs={12} md={3} key={index}>
                                        <Box
                                            component="img"
                                            src={data?.thump_image_path}
                                            sx={{
                                                width: "100%",
                                                height: 160,
                                                objectFit: "cover",
                                                borderRadius: "16px",
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => {
                                                localStorage.setItem('activeItem', 'episodes');
                                                navigate('/codegenie/generated-blogs');
                                            }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                    </Paper>

                </Box>
            </Box>

            {/* ================= VIDEO MODAL ================= */}
            <Modal open={helperPopup} onClose={() => setHelperPopup(false)} disableEscapeKeyDown>
                <Box
                    sx={{
                        position: "absolute" as "absolute",
                        top: "50%",
                        left: "50%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        transform: "translate(-50%, -50%)",
                        width: isMobile ? "95%" : "70%",
                        bgcolor: "background.paper",
                        border: "2px solid #000",
                        boxShadow: 24,
                        pt: 2,
                        px: 4,
                        pb: 3,
                        borderRadius: "15px",
                        padding: "3%",
                        fontFamily: 'Inter, sans-serif',
                    }}
                >
                    {embedSrc ? (
                        <iframe
                            width="100%"
                            height="500"
                            src={embedSrc}
                            title="Help Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    ) : helpervideosrc ? (
                        <iframe
                            width="100%"
                            height="500"
                            src={helpervideosrc}
                            title="Help Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    ) : (
                        <div
                            style={{
                                width: "100%",
                                height: 500,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                fontFamily: 'Inter, sans-serif',
                            }}
                        >
                            <div>
                                <p>Invalid video URL or embedding not allowed.</p>
                                {helpervideosrc && (
                                    <a href={helpervideosrc} target="_blank" rel="noopener noreferrer">
                                        Open in YouTube
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                    <Stack flexDirection="row" sx={{ mt: 3 }}>
                        <Button
                            onClick={() => setHelperPopup(false)}
                            variant="contained"
                            sx={{ mr: 2, fontFamily: 'Inter, sans-serif' }}
                        >
                            Close
                        </Button>
                    </Stack>
                </Box>
            </Modal>
        </>
    );
}